import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Archive, ArchiveRestore, ArrowLeft, Loader2, Camera, X, CheckCircle2, AlertTriangle, Clock, BarChart3, Truck } from "lucide-react";
import { ASSET_360_TABS } from "../../components/Asset360";
import { api, fetchPhotoBlob, formatApiError } from "../../lib/api";
import { useForm } from "../../components/forms/useForm";
import { Field, TextInput, NumberInput, DateInput, SelectInput, Textarea, fieldClasses } from "../../components/forms/Field";
import { required, numberNonNegative, yearReasonable, compose } from "../../lib/validation";
import EmptyState from "../../components/EmptyState";
import dict from "../../i18n/es-MX";

const fmtMXN = (n) => n == null ? "—" : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n);

function daysUntil(iso) {
    if (!iso) return null;
    const d = new Date(iso); if (isNaN(d)) return null;
    return Math.floor((d - new Date()) / 86400000);
}

function ExpiryCell({ asset }) {
    const a = daysUntil(asset.insurance_expiry), b = daysUntil(asset.registration_expiry);
    const days = [a, b].filter(x => x != null).sort((x,y)=>x-y)[0];
    if (days == null) return <span className="text-slate-500">—</span>;
    if (days < 0) return <span className="text-red-400 font-semibold" data-testid="expiry-expired">{dict.assets.expired}</span>;
    if (days <= 30) return <span className="text-brand-orange font-semibold" data-testid="expiry-soon">{days} {dict.assets.expiringSoon}</span>;
    return <span className="text-slate-400">{days} {dict.assets.daysUnit}</span>;
}

function StatusBadge({ status }) {
    const map = { disponible:"bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
        en_trabajo:"bg-sky-500/15 text-sky-300 border-sky-500/40",
        en_mantenimiento:"bg-amber-500/15 text-amber-300 border-amber-500/40",
        fuera_servicio:"bg-red-500/15 text-red-300 border-red-500/40" };
    return <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${map[status]||""}`}>{dict.assets.status[status] || status}</span>;
}

export function Toast({ msg, type="success", onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 2800); return () => clearTimeout(t); }, [onClose]);
    return <div data-testid="toast" className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow-lg z-50 ${type==="error"?"bg-red-700 text-white":"bg-emerald-700 text-white"}`}>{msg}</div>;
}

export function ConfirmDialog({ open, title, onConfirm, onCancel }) {
    if (!open) return null;
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog">
        <div className="absolute inset-0 bg-black/70" onClick={onCancel}/>
        <div className="relative w-full max-w-sm rounded-2xl bg-slate-900 border border-slate-800 p-5">
            <p className="text-sm mb-5">{title}</p>
            <div className="flex justify-end gap-2">
                <button onClick={onCancel} className="h-9 px-4 rounded border border-slate-700 text-sm" data-testid="confirm-cancel">{dict.common.cancel}</button>
                <button onClick={onConfirm} className="h-9 px-4 rounded bg-brand-orange text-slate-950 font-semibold text-sm" data-testid="confirm-ok">{dict.common.confirm}</button>
            </div>
        </div>
    </div>;
}

export function PhotoThumb({ photoId, size=80 }) {
    const [url, setUrl] = useState(null);
    useEffect(() => { let active = true;
        if (!photoId) { setUrl(null); return; }
        fetchPhotoBlob(photoId).then(u => { if (active) setUrl(u); }).catch(() => {});
        return () => { active = false; if (url) URL.revokeObjectURL(url); };
    }, [photoId]); // eslint-disable-line
    if (!photoId) return <div className="bg-slate-800 rounded flex items-center justify-center text-slate-600" style={{width:size,height:size}}><Camera size={size*0.4}/></div>;
    return url ? <img src={url} alt="" className="rounded object-cover" style={{width:size,height:size}}/> : <div className="bg-slate-800 rounded animate-pulse" style={{width:size,height:size}}/>;
}

// ============================================================
// ASSETS
// ============================================================

export function AssetsList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [subtypes, setSubtypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        kind: "", subtype: "", archived: false,
        // Multi-select status (default = disponible + en_trabajo)
        statuses: { disponible: true, en_trabajo: true, en_mantenimiento: false, fuera_servicio: false },
    });
    const [sortByUrgency, setSortByUrgency] = useState(false);
    const [toast, setToast] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.kind) params.kind = filters.kind;
            if (filters.subtype) params.subtype = filters.subtype;
            if (filters.archived) params.archived = true;
            const [a, s] = await Promise.all([api.get("/assets",{params}), api.get("/asset-subtypes")]);
            setItems(a.data); setSubtypes(s.data);
        } catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const sorted = useMemo(() => {
        // Apply multi-select status filter client-side.
        const activeStatuses = Object.entries(filters.statuses).filter(([,v])=>v).map(([k])=>k);
        const filtered = activeStatuses.length === 0
            ? items
            : items.filter(a => activeStatuses.includes(a.status));
        if (!sortByUrgency) return filtered;
        return [...filtered].sort((a,b) => {
            const da = Math.min(daysUntil(a.insurance_expiry) ?? 9999, daysUntil(a.registration_expiry) ?? 9999);
            const db = Math.min(daysUntil(b.insurance_expiry) ?? 9999, daysUntil(b.registration_expiry) ?? 9999);
            return da - db;
        });
    }, [items, sortByUrgency, filters.statuses]);

    const doArchive = async (id, archived) => {
        try {
            await api.post(`/assets/${id}/${archived?"restore":"archive"}`);
            setToast({msg: archived?dict.common.restore:dict.common.archived, type:"success"});
            load();
        } catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        setConfirm(null);
    };

    return <div data-testid="assets-page" className="space-y-5">
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div><h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.assets.title}</h1>
                <p className="mt-2 text-slate-400 text-sm">{dict.assets.subtitle}</p></div>
            <button onClick={()=>navigate("/activos/nuevo")} data-testid="new-asset-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase tracking-wider text-sm hover:bg-orange-400"><Plus size={18}/>{dict.assets.newAsset}</button>
        </header>

        <div className="flex flex-wrap gap-3 items-end">
            <div><label className="block text-xs uppercase text-slate-400 mb-1">{dict.assets.filters.kind}</label>
                <select value={filters.kind} onChange={e=>setFilters({...filters,kind:e.target.value})} data-testid="filter-kind" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                    <option value="">{dict.common.all}</option><option value="camion">{dict.assets.kinds.camion}</option><option value="maquinaria">{dict.assets.kinds.maquinaria}</option></select></div>
            <div><label className="block text-xs uppercase text-slate-400 mb-1">{dict.assets.filters.status}</label>
                <div className="flex flex-wrap gap-1" data-testid="filter-status-multi">
                    {Object.entries(dict.assets.status).map(([k,v]) => {
                        const checked = !!filters.statuses[k];
                        return (
                            <button key={k} type="button" onClick={() => setFilters({...filters, statuses:{...filters.statuses,[k]:!checked}})}
                                data-testid={`filter-status-${k}`}
                                className={`h-9 px-3 rounded-full text-[11px] uppercase tracking-wider border ${checked ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 border-slate-700 text-slate-300"}`}>
                                {v}
                            </button>
                        );
                    })}
                </div></div>
            <div><label className="block text-xs uppercase text-slate-400 mb-1">{dict.assets.filters.subtype}</label>
                <select value={filters.subtype} onChange={e=>setFilters({...filters,subtype:e.target.value})} data-testid="filter-subtype" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                    <option value="">{dict.common.all}</option>
                    {subtypes.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
            <label className="flex items-center gap-2 text-sm text-slate-300 h-10"><input type="checkbox" checked={filters.archived} onChange={e=>setFilters({...filters,archived:e.target.checked})} data-testid="filter-archived"/>{dict.common.showArchived}</label>
            <label className="flex items-center gap-2 text-sm text-slate-300 h-10"><input type="checkbox" checked={sortByUrgency} onChange={e=>setSortByUrgency(e.target.checked)} data-testid="sort-urgency"/>{dict.assets.sortByUrgency}</label>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="assets-table">
                <thead className="bg-slate-900/80 text-xs uppercase tracking-wider text-slate-400">
                    <tr><th className="text-left px-4 py-3">{dict.assets.cols.nickname}</th><th className="text-left px-4 py-3">{dict.assets.cols.kind}</th><th className="text-left px-4 py-3">{dict.assets.cols.subtype}</th><th className="text-left px-4 py-3">{dict.assets.cols.plate}</th><th className="text-left px-4 py-3">{dict.assets.cols.status}</th><th className="text-left px-4 py-3">{dict.assets.cols.expires}</th><th className="text-right px-4 py-3">{dict.common.actions}</th></tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && sorted.length===0 && <tr><td colSpan={7} className="px-0 py-0"><EmptyState icon={Truck} title={dict.assets.empty} ctaLabel={dict.assets.newAsset} ctaHref="/activos/nuevo" testid="assets-empty"/></td></tr>}
                    {!loading && sorted.map(a => <tr key={a.id} data-testid={`asset-row-${a.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                        <td className="px-4 py-3"><Link to={`/activos/${a.id}`} className="text-slate-100 hover:text-brand-orange font-medium">{a.nickname||"—"}</Link></td>
                        <td className="px-4 py-3 text-slate-300">{dict.assets.kinds[a.kind]}</td>
                        <td className="px-4 py-3 text-slate-300">{a.subtype||"—"}</td>
                        <td className="px-4 py-3 text-slate-300">{a.plate_or_economic_number||"—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={a.status}/></td>
                        <td className="px-4 py-3"><ExpiryCell asset={a}/></td>
                        <td className="px-4 py-3"><div className="flex justify-end gap-1">
                            <Link to={`/activos/${a.id}/editar`} data-testid={`edit-asset-${a.id}`} className="p-2 rounded hover:bg-slate-800 text-slate-300"><Pencil size={16}/></Link>
                            <button onClick={()=>setConfirm({id:a.id, archived:a.archived, title:a.archived?dict.common.confirmRestore:dict.common.confirmArchive})} data-testid={`archive-asset-${a.id}`} className={`p-2 rounded hover:bg-slate-800 ${a.archived?"text-emerald-400":"text-red-400"}`}>{a.archived?<ArchiveRestore size={16}/>:<Archive size={16}/>}</button>
                        </div></td>
                    </tr>)}
                </tbody>
            </table>
        </div>

        <ConfirmDialog open={!!confirm} title={confirm?.title} onCancel={()=>setConfirm(null)} onConfirm={()=>doArchive(confirm.id, confirm.archived)}/>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

function FinancingSection({ form }) {
    const v = form.values.financing || { mode:"efectivo" };
    const setF = (patch) => form.set("financing", { ...v, ...patch });
    const monthsSince = v.start_date ? Math.max(0, Math.floor((new Date() - new Date(v.start_date))/(30.4*86400000))) : 0;
    const paid = (Number(v.down_payment_mxn)||0) + (Number(v.monthly_payment_mxn)||0)*monthsSince;
    const remaining = v.mode==="financiado" && v.total_mxn != null ? Math.max(0, Number(v.total_mxn) - paid) : null;
    return <div className="space-y-4">
        <Field label={dict.assets.fields.financingMode}>
            <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="fmode" checked={v.mode==="efectivo"} onChange={()=>setF({mode:"efectivo"})} data-testid="fin-mode-efectivo"/>{dict.assets.fields.efectivo}</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="fmode" checked={v.mode==="financiado"} onChange={()=>setF({mode:"financiado"})} data-testid="fin-mode-financiado"/>{dict.assets.fields.financiado}</label>
            </div>
        </Field>
        {v.mode==="financiado" && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={dict.assets.fields.lender}><TextInput value={v.lender||""} onChange={e=>setF({lender:e.target.value})} data-testid="fin-lender"/></Field>
            <Field label={dict.assets.fields.total}><NumberInput value={v.total_mxn??""} onChange={e=>setF({total_mxn:e.target.value===""?null:Number(e.target.value)})} data-testid="fin-total"/></Field>
            <Field label={dict.assets.fields.down}><NumberInput value={v.down_payment_mxn??""} onChange={e=>setF({down_payment_mxn:e.target.value===""?null:Number(e.target.value)})}/></Field>
            <Field label={dict.assets.fields.monthly}><NumberInput value={v.monthly_payment_mxn??""} onChange={e=>setF({monthly_payment_mxn:e.target.value===""?null:Number(e.target.value)})}/></Field>
            <Field label={dict.assets.fields.rate}><NumberInput step="0.1" value={v.interest_rate??""} onChange={e=>setF({interest_rate:e.target.value===""?null:Number(e.target.value)})}/></Field>
            <Field label={dict.assets.fields.term}><NumberInput value={v.term_months??""} onChange={e=>setF({term_months:e.target.value===""?null:Number(e.target.value)})}/></Field>
            <Field label={dict.assets.fields.startDate}><DateInput value={(v.start_date||"").slice(0,10)} onChange={e=>setF({start_date: e.target.value? new Date(e.target.value).toISOString() : null})}/></Field>
            <div className="rounded-lg border border-brand-orange/40 bg-brand-orange/5 p-3" data-testid="remaining-balance">
                <div className="text-xs uppercase text-slate-400">{dict.assets.fields.remainingBalance}</div>
                <div className="font-display text-2xl font-bold text-brand-orange mt-1">{fmtMXN(remaining)}</div>
            </div>
        </div>}
    </div>;
}

export function AssetForm() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const [subtypes, setSubtypes] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [toast, setToast] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);

    const form = useForm({
        initial: { kind:"camion", subtype:"", nickname:"", plate_or_economic_number:"", brand:"", model:"", year:"", vin_serial:"",
            odometer_km:"", hour_meter_horas:"", photo_id:null, status:"disponible",
            financing:{mode:"efectivo"}, insurance_expiry:"", registration_expiry:"", photo_checklist_config_id:"" },
        validators: { nickname: required, kind: required, status: required, year: yearReasonable,
            odometer_km: numberNonNegative, hour_meter_horas: numberNonNegative,
            financing: (v) => null,
        },
    });

    useEffect(() => {
        Promise.all([api.get("/asset-subtypes"), api.get("/photo-checklists")])
            .then(([s,c]) => { setSubtypes(s.data); setChecklists(c.data); });
        if (isEdit) api.get(`/assets/${id}`).then(r => {
            const a = r.data;
            form.setValues({ ...a, year: a.year||"", odometer_km: a.odometer_km??"", hour_meter_horas: a.hour_meter_horas??"",
                insurance_expiry: (a.insurance_expiry||"").slice(0,10), registration_expiry: (a.registration_expiry||"").slice(0,10),
                photo_checklist_config_id: a.photo_checklist_config_id||"", financing: a.financing||{mode:"efectivo"} });
        }).catch(() => setToast({msg:dict.common.loadError,type:"error"}));
    }, [id]); // eslint-disable-line

    const onUploadPhoto = async (e) => {
        const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
        setPhotoUploading(true);
        const fd = new FormData(); fd.append("file", file); fd.append("kind","asset_main");
        try { const r = await api.post("/photos/upload", fd); form.set("photo_id", r.data.id); }
        catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
        finally { setPhotoUploading(false); }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        await form.handleSubmit(async (v) => {
            // cross-field rule: at least one meter must be present
            if ((v.odometer_km === "" || v.odometer_km === null) && (v.hour_meter_horas === "" || v.hour_meter_horas === null)) {
                setToast({ msg: dict.validation.atLeastOneMeter, type: "error" });
                return;
            }
            const body = { ...v,
                year: v.year===""?null:Number(v.year),
                odometer_km: v.odometer_km===""?null:Number(v.odometer_km),
                hour_meter_horas: v.hour_meter_horas===""?null:Number(v.hour_meter_horas),
                insurance_expiry: v.insurance_expiry? new Date(v.insurance_expiry).toISOString():null,
                registration_expiry: v.registration_expiry? new Date(v.registration_expiry).toISOString():null,
                photo_checklist_config_id: v.photo_checklist_config_id || null,
            };
            try {
                if (isEdit) await api.patch(`/assets/${id}`, body);
                else await api.post("/assets", body);
                setToast({msg:dict.common.saved,type:"success"});
                setTimeout(()=>navigate("/activos"), 600);
            } catch (err) { setToast({msg:formatApiError(err)||dict.common.saveError,type:"error"}); }
        });
    };

    return <div className="space-y-5 max-w-3xl">
        <Link to="/activos" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.assets.title}</Link>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">{isEdit?dict.assets.editAsset:dict.assets.newAsset}</h1>

        <form onSubmit={onSubmit} noValidate data-testid="asset-form" className="space-y-6">
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.ident}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.assets.fields.kind} required error={form.errors.kind}>
                        <SelectInput {...form.field("kind")} error={form.errors.kind} options={[{value:"camion",label:dict.assets.kinds.camion},{value:"maquinaria",label:dict.assets.kinds.maquinaria}]}/>
                    </Field>
                    <Field label={dict.assets.fields.subtype}>
                        <SelectInput {...form.field("subtype")}>
                            <option value="">—</option>
                            {subtypes.filter(s=>s.asset_kind===form.values.kind).map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                        </SelectInput>
                    </Field>
                    <Field label={dict.assets.fields.nickname} required error={form.errors.nickname}><TextInput {...form.field("nickname")} error={form.errors.nickname} data-testid="asset-nickname"/></Field>
                    <Field label={dict.assets.fields.plate}><TextInput {...form.field("plate_or_economic_number")} data-testid="asset-plate"/></Field>
                    <Field label={dict.assets.fields.brand}><TextInput {...form.field("brand")}/></Field>
                    <Field label={dict.assets.fields.model}><TextInput {...form.field("model")}/></Field>
                    <Field label={dict.assets.fields.year} error={form.errors.year}><NumberInput {...form.field("year")} error={form.errors.year}/></Field>
                    <Field label={dict.assets.fields.vin}><TextInput {...form.field("vin_serial")}/></Field>
                </div>
                <Field label={dict.assets.fields.photo}>
                    <div className="flex items-center gap-3">
                        <PhotoThumb photoId={form.values.photo_id} size={80}/>
                        <label className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-700 hover:border-brand-orange cursor-pointer text-sm">
                            {photoUploading? <Loader2 className="animate-spin" size={14}/> : <Camera size={14}/>}{dict.operator.uploadPhoto}
                            <input type="file" accept="image/*" onChange={onUploadPhoto} className="hidden" data-testid="asset-photo-input"/>
                        </label>
                        {form.values.photo_id && <button type="button" onClick={()=>form.set("photo_id",null)} className="text-red-400 text-sm">{dict.operator.clear}</button>}
                    </div>
                </Field>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.meters}</h2>
                <p className="text-xs text-slate-500">{dict.assets.fields.metersHint}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.assets.fields.odometer} error={form.errors.odometer_km}><NumberInput {...form.field("odometer_km")} error={form.errors.odometer_km} data-testid="asset-odometer"/></Field>
                    <Field label={dict.assets.fields.hourMeter} error={form.errors.hour_meter_horas}><NumberInput {...form.field("hour_meter_horas")} error={form.errors.hour_meter_horas} data-testid="asset-hourmeter"/></Field>
                </div>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.status}</h2>
                <Field label={dict.assets.fields.status}>
                    <SelectInput {...form.field("status")} options={Object.entries(dict.assets.status).map(([k,v])=>({value:k,label:v}))}/>
                </Field>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.ownership}</h2>
                <FinancingSection form={form}/>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.expiries}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.assets.fields.insuranceExpiry}><DateInput {...form.field("insurance_expiry")}/></Field>
                    <Field label={dict.assets.fields.regExpiry}><DateInput {...form.field("registration_expiry")}/></Field>
                </div>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.assets.sections.checklist}</h2>
                <Field label={dict.assets.fields.checklistConfig} hint={dict.assets.fields.useDefault}>
                    <SelectInput {...form.field("photo_checklist_config_id")}>
                        <option value="">{dict.assets.fields.useDefault}</option>
                        {checklists.map(c => <option key={c.id} value={c.id}>{c.asset_subtype}</option>)}
                    </SelectInput>
                </Field>
            </section>

            <div className="flex justify-end gap-2">
                <Link to="/activos" className="h-11 px-5 rounded-lg border border-slate-700 hover:bg-slate-800 inline-flex items-center text-sm">{dict.common.cancel}</Link>
                <button type="submit" disabled={form.submitting} data-testid="asset-submit" className="h-11 px-5 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
                    {form.submitting && <Loader2 className="animate-spin" size={16}/>}{form.submitting?dict.common.saving:dict.common.save}</button>
            </div>
        </form>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

function Sparkline({ points, width = 280, height = 60, stroke = "#fb923c" }) {
    if (!points || points.length === 0) {
        return <div className="text-xs text-slate-500" data-testid="sparkline-empty">{dict.assets.meterHistory.empty}</div>;
    }
    if (points.length === 1) {
        // Single point — render only the dot with explanatory hint, no polyline.
        const p = points[0];
        const cx = width / 2, cy = height / 2;
        const color = p.verified ? "#34d399" : p.flagged ? "#fb923c" : "#94a3b8";
        return (
            <div className="space-y-1" data-testid="sparkline-single">
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-16">
                    <circle cx={cx} cy={cy} r={4} fill={color}/>
                </svg>
                <div className="text-xs text-slate-500">{dict.assets.meterHistory.singlePointHint}</div>
            </div>
        );
    }
    const xs = points.map((_, i) => i);
    const ys = points.map(p => p.value);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const rangeY = maxY - minY || 1;
    const px = (i) => (i / (points.length - 1)) * (width - 4) + 2;
    const py = (v) => height - 4 - ((v - minY) / rangeY) * (height - 8);
    const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${px(i).toFixed(1)},${py(p.value).toFixed(1)}`).join(" ");
    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-16" data-testid="sparkline">
            <path d={d} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
            {points.map((p, i) => (
                <circle key={i} cx={px(i)} cy={py(p.value)} r={2.5}
                    fill={p.verified ? "#34d399" : p.flagged ? "#fb923c" : "#94a3b8"}/>
            ))}
        </svg>
    );
}

function MeterStatusBadge({ r }) {
    if (r.verified) return <span data-testid={`mh-badge-${r.id}`} className="px-2 h-5 text-[10px] rounded-full border inline-flex items-center gap-1 uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border-emerald-500/40"><CheckCircle2 size={10}/> {dict.verify.verifiedBadge}</span>;
    if (r.flagged) return <span data-testid={`mh-badge-${r.id}`} className="px-2 h-5 text-[10px] rounded-full border inline-flex items-center gap-1 uppercase tracking-wider bg-orange-500/15 text-orange-300 border-orange-500/40"><AlertTriangle size={10}/> {dict.verify.flaggedBadge}</span>;
    return <span data-testid={`mh-badge-${r.id}`} className="px-2 h-5 text-[10px] rounded-full border inline-flex items-center gap-1 uppercase tracking-wider bg-slate-700/40 text-slate-300 border-slate-500/40"><Clock size={10}/> {dict.verify.pendingBadge}</span>;
}

function MeterHistoryTab({ assetId }) {
    const [rows, setRows] = useState([]);
    const [kindFilter, setKindFilter] = useState("");
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        const params = kindFilter ? { kind: kindFilter } : {};
        api.get(`/assets/${assetId}/meter-history`, { params })
            .then(r => setRows(r.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [assetId, kindFilter]);

    if (loading) return <div className="text-center py-6"><Loader2 className="animate-spin inline text-slate-500"/></div>;

    const points = rows.map(r => ({ value: r.value, verified: r.verified, flagged: r.flagged }));

    return (
        <div className="space-y-4" data-testid="meter-history-tab">
            <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.assets.meterHistory.kindFilter}:</span>
                {[
                    { v: "", l: dict.assets.meterHistory.all },
                    { v: "odometer_km", l: "km" },
                    { v: "hour_meter_horas", l: "h" },
                ].map(opt => (
                    <button key={opt.v} type="button"
                        onClick={() => setKindFilter(opt.v)}
                        data-testid={`mh-filter-${opt.v || "all"}`}
                        className={`px-2.5 h-7 text-[11px] rounded-md border uppercase tracking-wider ${kindFilter === opt.v ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>
                        {opt.l}
                    </button>
                ))}
            </div>
            {rows.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-500" data-testid="mh-empty">{dict.assets.meterHistory.empty}</div>
            ) : (
                <>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{dict.assets.meterHistory.trendTitle}</div>
                        <Sparkline points={points}/>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80">
                                <tr>
                                    <th className="text-left py-2 px-3">{dict.assets.meterHistory.colDate}</th>
                                    <th className="text-left py-2 px-3">{dict.assets.meterHistory.colKind}</th>
                                    <th className="text-right py-2 px-3">{dict.assets.meterHistory.colValue}</th>
                                    <th className="text-right py-2 px-3">{dict.assets.meterHistory.colDelta}</th>
                                    <th className="text-left py-2 px-3">{dict.assets.meterHistory.colStatus}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.slice().reverse().map((r, idx) => {
                                    const prev = rows[rows.length - 2 - idx];
                                    const delta = prev && prev.kind === r.kind ? r.value - prev.value : null;
                                    return (
                                        <tr key={r.id} className="border-t border-slate-800" data-testid={`mh-row-${r.id}`}>
                                            <td className="py-2 px-3 text-slate-400">{r.taken_at ? new Date(r.taken_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                                            <td className="py-2 px-3 text-xs uppercase tracking-wider text-slate-400">{r.kind === "hour_meter_horas" ? "horas" : "km"}</td>
                                            <td className="py-2 px-3 text-right font-mono text-slate-100">{r.value.toLocaleString("es-MX")}</td>
                                            <td className="py-2 px-3 text-right font-mono text-xs text-slate-400">{delta != null ? (delta > 0 ? "+" : "") + delta.toLocaleString("es-MX") : "—"}</td>
                                            <td className="py-2 px-3"><MeterStatusBadge r={r}/></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Utilization tab
// ---------------------------------------------------------------------------

function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function lastOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function toISODate(d) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function monthLabel(d) {
    return d.toLocaleDateString("es-MX", { month: "short" }).replace(".", "");
}

function UtilBarChart({ series }) {
    const W = 560, H = 140, padX = 28, padB = 26, padT = 12;
    const innerW = W - padX * 2, innerH = H - padT - padB;
    const n = series.length;
    const barW = (innerW / n) * 0.6;
    const step = innerW / n;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36" data-testid="util-chart">
            {[0, 25, 50, 75, 100].map(g => {
                const y = padT + innerH - (g / 100) * innerH;
                return (
                    <g key={g}>
                        <line x1={padX} x2={W - padX} y1={y} y2={y} stroke="#1e293b" strokeWidth={1}/>
                        <text x={padX - 6} y={y + 3} fontSize="9" fill="#64748b" textAnchor="end">{g}</text>
                    </g>
                );
            })}
            {series.map((s, i) => {
                const cx = padX + step * (i + 0.5);
                const pct = Math.max(0, Math.min(100, s.utilization_pct || 0));
                const h = (pct / 100) * innerH;
                const y = padT + innerH - h;
                const color = pct < 40 ? "#fb923c" : pct < 70 ? "#facc15" : "#34d399";
                return (
                    <g key={i}>
                        <rect x={cx - barW / 2} y={y} width={barW} height={h} fill={color} rx={2}>
                            <title>{`${s._label}: ${pct.toFixed(1)}%`}</title>
                        </rect>
                        <text x={cx} y={H - 10} fontSize="9" fill="#94a3b8" textAnchor="middle">{s._label}</text>
                    </g>
                );
            })}
        </svg>
    );
}

function UtilizationTab({ assetId }) {
    const today = new Date();
    const defFrom = firstOfMonth(today);
    const defTo = today;
    const [range, setRange] = useState({ from: toISODate(defFrom), to: toISODate(defTo) });
    const [current, setCurrent] = useState(null);
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const loadCurrent = useCallback(async (fromStr, toStr) => {
        try {
            const r = await api.get("/finance/asset-utilization", {
                params: { asset_id: assetId, from: fromStr, to: toStr },
            });
            setCurrent(r.data);
        } catch (e) { setErr(dict.assets.utilization.loadError); }
    }, [assetId]);

    const loadSeries = useCallback(async () => {
        try {
            const calls = [];
            const labels = [];
            for (let i = 11; i >= 0; i--) {
                const ref = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const f = toISODate(firstOfMonth(ref));
                const t = toISODate(lastOfMonth(ref));
                labels.push(monthLabel(ref));
                calls.push(api.get("/finance/asset-utilization", { params: { asset_id: assetId, from: f, to: t } }));
            }
            const results = await Promise.all(calls);
            setSeries(results.map((r, idx) => ({ ...r.data, _label: labels[idx] })));
        } catch (e) {
            setErr(dict.assets.utilization.loadError);
        }
    }, [assetId]); // eslint-disable-line

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr("");
        Promise.all([loadCurrent(range.from, range.to), loadSeries()])
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [loadCurrent, loadSeries, range.from, range.to]);

    const applyPreset = (kind) => {
        const now = new Date();
        if (kind === "month") setRange({ from: toISODate(firstOfMonth(now)), to: toISODate(now) });
        else if (kind === "last30") {
            const f = new Date(now); f.setDate(f.getDate() - 29);
            setRange({ from: toISODate(f), to: toISODate(now) });
        } else if (kind === "ytd") setRange({ from: toISODate(new Date(now.getFullYear(), 0, 1)), to: toISODate(now) });
    };

    if (loading && !current) return <div className="text-center py-10"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (err && !current) return <div className="text-sm text-red-300 py-6" data-testid="util-error">{err}</div>;

    const kpi = current || { days_in_use: 0, days_in_maintenance: 0, days_available: 0, utilization_pct: 0, productive_pct: 0 };

    return (
        <div className="space-y-5" data-testid="utilization-tab">
            {/* Range selector */}
            <div className="flex flex-wrap gap-2 items-end p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.assets.utilization.from}</label>
                    <input type="date" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })}
                        data-testid="util-from"
                        className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.assets.utilization.to}</label>
                    <input type="date" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })}
                        data-testid="util-to"
                        className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
                <div className="flex flex-wrap gap-1 ml-auto">
                    <button type="button" onClick={() => applyPreset("month")} data-testid="util-preset-month"
                        className="h-9 px-3 rounded-lg border border-slate-700 hover:border-brand-orange text-xs uppercase tracking-wider text-slate-300">
                        {dict.assets.utilization.presetThisMonth}
                    </button>
                    <button type="button" onClick={() => applyPreset("last30")} data-testid="util-preset-30"
                        className="h-9 px-3 rounded-lg border border-slate-700 hover:border-brand-orange text-xs uppercase tracking-wider text-slate-300">
                        {dict.assets.utilization.presetLast30}
                    </button>
                    <button type="button" onClick={() => applyPreset("ytd")} data-testid="util-preset-ytd"
                        className="h-9 px-3 rounded-lg border border-slate-700 hover:border-brand-orange text-xs uppercase tracking-wider text-slate-300">
                        {dict.assets.utilization.presetYTD}
                    </button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="util-kpi-inuse">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.assets.utilization.kpiDaysInUse}</div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-emerald-300">{kpi.days_in_use}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="util-kpi-maint">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.assets.utilization.kpiDaysMaint}</div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-amber-300">{kpi.days_in_maintenance}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="util-kpi-avail">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.assets.utilization.kpiDaysAvail}</div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-slate-200">{kpi.days_available}</div>
                </div>
                <div className={`rounded-xl border p-4 ${kpi.utilization_pct < 40 ? "border-brand-orange/50 bg-brand-orange/5" : "border-slate-800 bg-slate-900/40"}`} data-testid="util-kpi-pct">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.assets.utilization.kpiUtilPct}</div>
                    <div className={`mt-2 font-display text-3xl font-extrabold ${kpi.utilization_pct < 40 ? "text-brand-orange" : "text-slate-100"}`}>
                        {kpi.utilization_pct?.toFixed(1)}%
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">{dict.assets.utilization.kpiProductivePct}: {kpi.productive_pct?.toFixed(1)}%</div>
                </div>
            </div>

            {/* Chart */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
                    <BarChart3 size={12} className="text-brand-orange"/> {dict.assets.utilization.chartTitle}
                </div>
                <div className="text-xs text-slate-500 mb-2">{dict.assets.utilization.chartHint}</div>
                {series.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-6" data-testid="util-chart-empty">{dict.assets.utilization.empty}</div>
                ) : (
                    <UtilBarChart series={series}/>
                )}
            </div>

            {/* Monthly table */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 px-4 pt-3">{dict.assets.utilization.tableTitle}</div>
                <table className="min-w-full text-sm mt-2" data-testid="util-table">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80">
                        <tr>
                            <th className="text-left py-2 px-3">{dict.assets.utilization.colMonth}</th>
                            <th className="text-right py-2 px-3">{dict.assets.utilization.colInUse}</th>
                            <th className="text-right py-2 px-3">{dict.assets.utilization.colMaint}</th>
                            <th className="text-right py-2 px-3">{dict.assets.utilization.colAvail}</th>
                            <th className="text-right py-2 px-3">{dict.assets.utilization.colUtil}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {series.map((s, i) => (
                            <tr key={i} className="border-t border-slate-800" data-testid={`util-row-${i}`}>
                                <td className="py-2 px-3 text-slate-300 uppercase text-xs">{s._label}</td>
                                <td className="py-2 px-3 text-right font-mono text-emerald-300">{s.days_in_use}</td>
                                <td className="py-2 px-3 text-right font-mono text-amber-300">{s.days_in_maintenance}</td>
                                <td className="py-2 px-3 text-right font-mono text-slate-300">{s.days_available}</td>
                                <td className={`py-2 px-3 text-right font-mono ${s.utilization_pct < 40 ? "text-brand-orange" : "text-slate-100"}`}>{s.utilization_pct?.toFixed(1)}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function AssetDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [a, setA] = useState(null);
    const [toast, setToast] = useState(null);
    const initialTab = (() => {
        const t = searchParams.get("tab");
        const all = ["overview", "meterHistory", "utilization", "jobs", "maintenance", "photos", "fuel", "costs"];
        return all.includes(t) ? t : "overview";
    })();
    const [tab, setTab] = useState(initialTab);
    useEffect(() => { api.get(`/assets/${id}`).then(r=>setA(r.data)).catch(()=>setToast({msg:dict.common.loadError,type:"error"})); }, [id]);
    const onChangeTab = (next) => {
        setTab(next);
        const sp = new URLSearchParams(searchParams);
        if (next === "overview") sp.delete("tab");
        else sp.set("tab", next);
        setSearchParams(sp, { replace: true });
    };
    if (!a) return <Loader2 className="animate-spin"/>;
    const Row = ({k,v}) => <div className="flex justify-between py-2 border-b border-slate-800 last:border-0"><span className="text-xs uppercase text-slate-500">{k}</span><span className="text-sm">{v||"—"}</span></div>;
    const tabsList = [
        { key: "overview", label: dict.assets.detail.tabs.overview },
        { key: "meterHistory", label: dict.assets.detail.tabs.meterHistory },
        { key: "utilization", label: dict.assets.detail.tabs.utilization },
        ...ASSET_360_TABS.map(t => ({ key: t.key, label: dict.asset360.tabs[t.key] })),
    ];
    const a360Tab = ASSET_360_TABS.find(t => t.key === tab);
    return <div data-testid="asset-detail" className="space-y-5 max-w-5xl">
        <Link to="/activos" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.assets.title}</Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
                <PhotoThumb photoId={a.photo_id} size={96}/>
                <div>
                    <h1 className="font-display text-4xl font-extrabold">{a.nickname}</h1>
                    <div className="mt-1 flex items-center gap-2 text-sm text-slate-400">{dict.assets.kinds[a.kind]} • {a.subtype||"—"} <StatusBadge status={a.status}/></div>
                </div>
            </div>
            <div className="flex gap-2">
                <Link to={`/activos/${a.id}/editar`} data-testid="detail-edit" className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm inline-flex items-center gap-1"><Pencil size={14}/>{dict.common.edit}</Link>
                <button onClick={async()=>{await api.post(`/assets/${a.id}/${a.archived?"restore":"archive"}`); navigate("/activos");}} data-testid="detail-archive" className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm inline-flex items-center gap-1">{a.archived?<ArchiveRestore size={14}/>:<Archive size={14}/>}{a.archived?dict.common.restore:dict.common.archive}</button>
            </div>
        </div>

        <div className="flex gap-1 border-b border-slate-800 overflow-x-auto">
            {tabsList.map(t => (
                <button key={t.key} type="button" onClick={() => onChangeTab(t.key)} data-testid={`asset-tab-${t.key}`}
                    className={`-mb-px px-3 h-9 text-xs font-semibold uppercase tracking-wider border-b-2 transition whitespace-nowrap ${tab === t.key ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                    {t.label}
                </button>
            ))}
        </div>

        {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="asset-overview-tab">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.assets.sections.ident}</h2>
                <Row k="Placa/Eco" v={a.plate_or_economic_number}/><Row k="Marca" v={a.brand}/><Row k="Modelo" v={a.model}/><Row k="Año" v={a.year}/><Row k="VIN" v={a.vin_serial}/>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.assets.sections.meters}</h2>
                <Row k="Odómetro" v={a.odometer_km!=null?`${a.odometer_km} km`:null}/>
                <Row k="Horímetro" v={a.hour_meter_horas!=null?`${a.hour_meter_horas} hrs`:null}/>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.assets.sections.ownership}</h2>
                <Row k="Modalidad" v={a.financing?.mode==="financiado"?dict.assets.fields.financiado:dict.assets.fields.efectivo}/>
                {a.financing?.mode==="financiado" && <>
                    <Row k="Acreedor" v={a.financing?.lender}/>
                    <Row k="Total" v={fmtMXN(a.financing?.total_mxn)}/>
                    <Row k="Pago mensual" v={fmtMXN(a.financing?.monthly_payment_mxn)}/>
                    <Row k={dict.assets.fields.remainingBalance} v={<span className="text-brand-orange font-semibold">{fmtMXN(a.remaining_balance_mxn)}</span>}/>
                </>}
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.assets.sections.expiries}</h2>
                <Row k="Seguro" v={a.insurance_expiry?new Date(a.insurance_expiry).toLocaleDateString("es-MX"):null}/>
                <Row k="Placas" v={a.registration_expiry?new Date(a.registration_expiry).toLocaleDateString("es-MX"):null}/>
            </div>
        </div>
        )}

        {tab === "meterHistory" && <MeterHistoryTab assetId={a.id}/>}

        {tab === "utilization" && <UtilizationTab assetId={a.id}/>}

        {a360Tab && <a360Tab.Component assetId={a.id}/>}

        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}
